import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { VERSION as BUILD_VERSION } from 'environments/version';
import { EMPTY, fromEvent, merge, Observable, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class VersionCheckService {
    // Version baked into the JS bundle at build time. This is the only safe
    // baseline: comparing the polled value against the bundle's own version
    // tells us with certainty whether the user is running stale code.
    private readonly buildVersion = BUILD_VERSION;

    constructor(private readonly http: HttpClient) {}

    /**
     * Polls `versionUrl` periodically and on tab-visibility / network-online
     * events, comparing the response against the version baked into the
     * current JS bundle. Emits `true` exactly once when staleness is first
     * detected (and only `true` thereafter — never reverts).
     */
    public initVersionCheck(versionUrl: string, frequency = 1000 * 60 * 5): Observable<boolean> {
        // Skip in local dev — the bundle is built with VERSION='dev' which
        // would never match a real `/version.json` and would flap the banner.
        // Return EMPTY (never emits) rather than of(false): the app-component
        // subscriber sets `updateAvailable = true` on *any* emission, so
        // emitting `false` here would wrongly show the banner in local dev.
        if (this.buildVersion === 'dev') {
            return EMPTY;
        }

        const periodic$ = timer(0, frequency);

        const visibility$ = fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden));

        const online$ = fromEvent(globalThis, 'online');

        return merge(periodic$, visibility$, online$).pipe(
            switchMap(() => this.checkVersion(versionUrl)),
            // Don't re-emit identical results back-to-back; once we say "true",
            // we never want to flicker back to "false" on a transient miss.
            distinctUntilChanged(),
            filter(updateAvailable => updateAvailable),
        );
    }

    private checkVersion(versionUrl: string): Observable<boolean> {
        // Cache-bust at every layer: query-string defeats CloudFront edge and
        // intermediate proxies; no-cache headers defeat the browser HTTP cache.
        const separator = versionUrl.includes('?') ? '&' : '?';
        const bustedUrl = `${versionUrl}${separator}t=${Date.now()}`;
        const headers = new HttpHeaders({
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
        });

        return this.http.get<{ version: string }>(bustedUrl, { headers }).pipe(
            map(response => {
                const polledVersion = response?.version;
                if (!polledVersion) {
                    return false;
                }
                return polledVersion !== this.buildVersion;
            }),
            // Network blips, 404s in misconfigured envs, etc. shouldn't be
            // surfaced as "update available" — just silently retry next tick.
            catchError(() => of(false)),
        );
    }
}
